import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Shield, Cloud, Sparkles, ArrowRight, Loader2 } from "lucide-react";
import supabase from "../supabase/supabase";
import { DocumentService } from "../services/DocumentService";
import { useAnalytics } from "../hooks/useAnalytics";

interface AuthPromptModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  customMessage?: string;
}

const AuthPromptModal: React.FC<AuthPromptModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  customMessage,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingApple, setIsLoadingApple] = useState(false);
  const [syncStatus, setSyncStatus] = useState<
    "idle" | "syncing" | "success" | "error"
  >("idle");
  const analytics = useAnalytics();

  const handleGoogleSignIn = async () => {
    setIsLoading(true);
    try {
      analytics.webappLoginSelected("google");
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
        },
      });

      if (error) throw error;
    } catch (error) {
      console.error("Sign in error:", error);
      setIsLoading(false);
    }
  };

  const handleAppleSignIn = async () => {
    setIsLoadingApple(true);
    try {
      analytics.webappLoginSelected("apple");
      // Web requires the Services ID, not the App Bundle ID
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: "apple",
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
          queryParams: {
            client_id: "com.writevoice.app.signin", // Services ID for web
          },
        },
      });

      if (error) throw error;
    } catch (error) {
      console.error("Apple sign in error:", error);
      setIsLoadingApple(false);
    }
  };

  const handleMaybeLater = () => {
    DocumentService.markAuthPrompted();
    onClose();
  };

  const benefits = [
    {
      icon: Cloud,
      title: "Never Lose Your Work",
      description: "Access your documents from any device, anytime",
      gradient: "from-blue-400 to-blue-600",
    },
    {
      icon: Shield,
      title: "Secure & Private",
      description: "Your data is encrypted and protected",
      gradient: "from-green-400 to-green-600",
    },
    {
      icon: Sparkles,
      title: "Unlock AI Features",
      description: "Get access to advanced rewriting and translation",
      gradient: "from-purple-400 to-purple-600",
    },
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 z-[100]"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{
              type: "spring",
              stiffness: 300,
              damping: 25,
            }}
            className="fixed inset-0 flex items-center justify-center z-[101] px-4"
          >
            <div className="w-full max-w-lg bg-white rounded-3xl shadow-2xl overflow-hidden">
              {/* Header with gradient */}
              <div className="relative px-8 pt-8 pb-6 bg-gradient-to-br from-sky-50 via-white to-purple-50">
                <motion.button
                  whileHover={{ scale: 1.1, rotate: 90 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={handleMaybeLater}
                  className="absolute top-6 right-6 w-8 h-8 rounded-full bg-white/80 hover:bg-white flex items-center justify-center transition-colors shadow-sm"
                >
                  <X className="w-4 h-4 text-gray-500" strokeWidth={2} />
                </motion.button>

                <motion.div
                  initial={{ y: -10, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.1 }}
                  className="text-center"
                >
                  <motion.div
                    className="w-16 h-16 bg-gradient-to-br from-sky-400 to-sky-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg"
                    animate={{
                      rotate: [0, 5, -5, 0],
                    }}
                    transition={{
                      duration: 4,
                      repeat: Infinity,
                      repeatType: "reverse",
                    }}
                  >
                    <Sparkles
                      className="w-8 h-8 text-white"
                      strokeWidth={1.5}
                    />
                  </motion.div>

                  <h2 className="text-2xl font-semibold text-gray-900 mb-2">
                    {customMessage ? "Unlock Premium Features 🌟" : "You're on a roll! 🚀"}
                  </h2>
                  <p className="text-gray-600 text-sm">
                    {customMessage || "Sign up to save your documents forever and unlock premium features"}
                  </p>
                </motion.div>
              </div>

              {/* Benefits */}
              <div className="px-8 py-6 space-y-4">
                {benefits.map((benefit, index) => (
                  <motion.div
                    key={benefit.title}
                    initial={{ x: -20, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: 0.2 + index * 0.1 }}
                    className="flex items-start space-x-3"
                  >
                    <div
                      className={`w-10 h-10 rounded-xl bg-gradient-to-br ${benefit.gradient} flex items-center justify-center flex-shrink-0`}
                    >
                      <benefit.icon
                        className="w-5 h-5 text-white"
                        strokeWidth={2}
                      />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-sm font-medium text-gray-900">
                        {benefit.title}
                      </h3>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {benefit.description}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Actions */}
              <div className="px-8 pb-8 space-y-3">
                {/* Google Sign In Button */}
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleGoogleSignIn}
                  disabled={isLoading || isLoadingApple}
                  className="w-full relative overflow-hidden group"
                >
                  <div className="relative px-6 py-3 bg-white border border-gray-200 rounded-2xl flex items-center justify-center space-x-3 hover:bg-gray-50 hover:border-gray-300 transition-all">
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                      <path
                        fill="#EA4335"
                        d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.43C17.95 1.19 15.23 0 12 0 7.31 0 3.26 2.69 1.28 6.61l3.99 3.09C6.21 6.86 8.87 4.75 12 4.75z"
                      />
                      <path
                        fill="#4285F4"
                        d="M23.49 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.47c-.29 1.48-1.14 2.73-2.4 3.58l3.86 3c2.26-2.09 3.56-5.17 3.56-8.82z"
                      />
                      <path
                        fill="#FBBC05"
                        d="M5.26 14.29c-.25-.73-.38-1.49-.38-2.29s.14-1.57.38-2.29L1.28 6.61A11.97 11.97 0 000 12c0 1.94.46 3.77 1.28 5.39l3.98-3.1z"
                      />
                      <path
                        fill="#34A853"
                        d="M12 24c3.24 0 5.95-1.07 7.93-2.91l-3.86-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96l-3.99 3.09C3.26 21.31 7.31 24 12 24z"
                      />
                    </svg>
                    <span className="text-gray-700 font-medium">
                      {isLoading ? "Signing in..." : "Continue with Google"}
                    </span>
                    {isLoading ? (
                      <Loader2 className="w-4 h-4 text-gray-700 animate-spin" />
                    ) : (
                      <ArrowRight className="w-4 h-4 text-gray-700" />
                    )}
                  </div>
                </motion.button>

                {/* Apple Sign In Button */}
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleAppleSignIn}
                  disabled={isLoadingApple || isLoading}
                  className="w-full relative overflow-hidden group"
                >
                  <div className="relative px-6 py-3 bg-white border border-gray-200 rounded-2xl flex items-center justify-center space-x-3 hover:bg-gray-50 hover:border-gray-300 transition-all">
                    <svg
                      className="w-5 h-5 text-gray-700"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                    </svg>
                    <span className="text-gray-700 font-medium">
                      {isLoadingApple ? "Signing in..." : "Continue with Apple"}
                    </span>
                    {isLoadingApple ? (
                      <Loader2 className="w-4 h-4 text-gray-700 animate-spin" />
                    ) : (
                      <ArrowRight className="w-4 h-4 text-gray-700" />
                    )}
                  </div>
                </motion.button>

                {/* Maybe Later Button */}
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleMaybeLater}
                  className="w-full px-6 py-3 text-gray-600 hover:text-gray-900 font-medium rounded-2xl hover:bg-gray-50 transition-colors"
                >
                  Maybe later
                </motion.button>
              </div>

              {/* Sync Status (shown during sync) */}
              <AnimatePresence>
                {syncStatus === "syncing" && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="border-t border-gray-100"
                  >
                    <div className="px-8 py-4 flex items-center justify-center space-x-2">
                      <Loader2 className="w-4 h-4 text-sky-500 animate-spin" />
                      <span className="text-sm text-gray-600">
                        Syncing your documents...
                      </span>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default AuthPromptModal;
